import { computed, inject, Injectable, signal } from '@angular/core';
import { ITodoItem, ITodoListEditItem, ITodoSection } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { FiContentLanguageSharedService } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared.service';
import { cloneDeep } from '@fi-shared/utils';
import { IAccessRightFilterCollection, IAccessRightSelectionFiltersCollection } from '@fi-app/shared/models';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';

@Injectable()
export class FiTodoListStoreService {
    private fiContentLanguageService = inject(FiContentLanguageSharedService);

    _selectedLanguageId = signal<number>(this.fiContentLanguageService.getDefaultContentLanguageId());
    readonly selectedLanguageId = this._selectedLanguageId.asReadonly();
    setSelectedLanguageId(value: number): void {
        this._selectedLanguageId.set(value);
    }

    private _businessUnitIdsOutOfScope = signal<number[]>([]);
    readonly businessUnitIdsOutOfScope = this._businessUnitIdsOutOfScope.asReadonly();
    setBusinessUnitIdsOutOfScope(value: number[]): void {
        this._businessUnitIdsOutOfScope.set(value);
    }

    private _todoListAccessRightFilterCollection = signal<IAccessRightFilterCollection | undefined>(undefined);
    readonly todoListAccessRightFilterCollection = this._todoListAccessRightFilterCollection.asReadonly();
    setTodoListAccessRightFilterCollection(value: IAccessRightFilterCollection): void {
        this._todoListAccessRightFilterCollection.set(value);
    }

    private _todoListQuickSelectionFilters = signal<IAccessRightSelectionFiltersCollection[]>([]);
    readonly todoListQuickSelectionFilters = this._todoListQuickSelectionFilters.asReadonly();
    setTodoListQuickSelectionFilters(value: IAccessRightSelectionFiltersCollection[]): void {
        this._todoListQuickSelectionFilters.set(value);
    }

    private _selectedFiltersCollection = signal<IAccessRightSelectionFiltersCollection | undefined>(undefined);
    readonly selectedFiltersCollection = this._selectedFiltersCollection.asReadonly();
    setSelectedFiltersCollection(value: IAccessRightSelectionFiltersCollection): void {
        this._selectedFiltersCollection.set(value);
    }

    _initialTodoListData = signal<ITodoListEditItem>(null);
    readonly initialTodoListData = this._initialTodoListData.asReadonly();
    setInitialTodoListData(value: ITodoListEditItem): void {
        this._initialTodoListData.set(cloneDeep(value));
    }

    _currentTodoListData = signal<ITodoListEditItem>(null);
    readonly currentTodoListData = this._currentTodoListData.asReadonly();
    setCurrentTodoListData(value: ITodoListEditItem): void {
        const todoListData = cloneDeep(value);
        this._currentTodoListData.set(todoListData);
        this.setTodoListSections(todoListData?.Sections);
    }
    readonly todoListConnectedOnboardings = computed<INameIdObj[]>(
        () => this.currentTodoListData()?.ConnectedOnboardings ?? [],
    );

    _currentTodoListSections = signal<ITodoSection[]>([]);
    readonly currentTodoListSections = this._currentTodoListSections.asReadonly();
    setTodoListSections(value: ITodoSection[] = []): void {
        this._currentTodoListSections.set(cloneDeep(value));
    }

    _currentTodoListItemIndex = signal<number>(null);
    readonly currentTodoListItemIndex = this._currentTodoListItemIndex.asReadonly();
    setCurrentTodoListItemIndex(value: number): void {
        this._currentTodoListItemIndex.set(value);
    }

    _currentTodoListSectionIndex = signal<number>(null);
    readonly currentTodoListSectionIndex = this._currentTodoListSectionIndex.asReadonly();
    setCurrentTodoListSectionIndex(value: number): void {
        this._currentTodoListSectionIndex.set(value);
    }

    _currentTodoListItem = signal<ITodoItem>(null);
    readonly currentTodoListItem = this._currentTodoListItem.asReadonly();
    setCurrentTodoListItem(value: ITodoItem): void {
        this._currentTodoListItem.set(value);
    }

    updateTodoListData(updater: (current: ITodoListEditItem) => ITodoListEditItem): void {
        this._currentTodoListData.update(updater);
    }

    updateTodoListSectionsOnTodoListData(sections: ITodoSection[]): void {
        this.updateTodoListData((current) => {
            return { ...current, Sections: cloneDeep(sections) };
        });
    }

    resetTodoListEditorStoreData(): void {
        this.setInitialTodoListData(null);
        this.setCurrentTodoListData(null);
        this.setTodoListSections([]);
        this.setSelectedLanguageId(this.fiContentLanguageService.getDefaultContentLanguageId());
        this.setCurrentTodoListItem(null);
        this.setCurrentTodoListSectionIndex(null);
        this.setCurrentTodoListItemIndex(null);
        this.setTodoListQuickSelectionFilters([]);
        this.setTodoListAccessRightFilterCollection(undefined);
        this.setSelectedFiltersCollection(undefined);
        this.setBusinessUnitIdsOutOfScope([]);
    }
}
